/**
 * Condition pill colour map per spec §6.4.
 *
 * Tailwind v4 in this project resolves CSS custom properties via the
 * `bg-foo` / `text-foo` utilities — but the design system has fixed
 * semantic variables (`--background`, `--foreground`, `--destructive`,
 * `--success`, `--warning`, `--info`, etc.). We map each condition to a
 * combination of these variables. No raw hex.
 */

import type { EquipmentCondition } from '../../api/equipment-tab-types'

export interface ConditionStyle {
  /** Tailwind classes for a small pill (background + border + text). */
  pill: string
  /** Tailwind classes for a thin progress bar fill. */
  bar: string
  /** Tailwind classes for the chevron-square seen in the rollup chips. */
  chip: string
}

/**
 * Visual treatment per condition. The CSS variables come from
 * `front/src/index.css` (theme tokens) — see the `--success` / `--warning`
 * / `--info` / `--destructive` definitions. `unassessed` is rendered with
 * the muted-foreground tone.
 */
export const CONDITION_STYLES: Record<EquipmentCondition, ConditionStyle> = {
  new: {
    pill: 'border-info/25 bg-info/10 text-info',
    bar: 'bg-info',
    chip: 'bg-info',
  },
  good: {
    pill: 'border-success/25 bg-success/10 text-success',
    bar: 'bg-success',
    chip: 'bg-success',
  },
  fair: {
    pill: 'border-warning/25 bg-warning/10 text-warning',
    bar: 'bg-warning',
    chip: 'bg-warning',
  },
  // The design system has no dedicated `orange` token; the spec asks for
  // orange on `poor` and red on `end_of_life`. We borrow the warning tone
  // for `poor` (already a yellow-orange in the palette) and stack a darker
  // shade variant via the destructive/60 mix for `end_of_life`.
  poor: {
    pill: 'border-warning/40 bg-warning/15 text-warning',
    bar: 'bg-warning',
    chip: 'bg-warning',
  },
  end_of_life: {
    pill: 'border-destructive/25 bg-destructive/10 text-destructive',
    bar: 'bg-destructive',
    chip: 'bg-destructive',
  },
  unassessed: {
    pill: 'border-muted-foreground/20 bg-muted text-muted-foreground',
    bar: 'bg-muted-foreground/40',
    chip: 'bg-muted-foreground/40',
  },
}

/** Numeric ordering for sort: worst first. Matches §6.4 sort key. */
export const CONDITION_SEVERITY: Record<EquipmentCondition, number> = {
  end_of_life: 5,
  poor: 4,
  fair: 3,
  good: 2,
  new: 1,
  unassessed: 0,
}
